import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { Filter, Grid, List, ChevronDown, ArrowLeft, ArrowRight } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ProductCard from '../components/ProductCard';
import FilterSearchInput from '../components/FilterSearchInput';
import apiService from '../services/nextlevelgames-api';
import { useStore } from '../context/StoreContext';
import pokemonLogo from '../assets/images/pokemon_logo.png';
import mtgLogo from '../assets/images/mtg_logo.png';
import warhammer40k from '../assets/images/warhammer_40k.png';
import marvelMtg from '../assets/images/marvel_mtg.png';

const categoryToApiFilter = {
  magic: ['Magic Singles'],
  pokemon: ['Pokemon Singles'],
  yugioh: ['Yugioh Singles'],
  onepiece: ['One Piece Singles'],
  pokemonjapan: ['Pokemon Japan Singles'],
  riftbound: ['Riftbound Singles'],
};

const subCategoryFilters = {
  magic: {
    label: 'Color',
    options: ['White', 'Blue', 'Black', 'Red', 'Green', 'Multicolor', 'Colorless', 'Land'],
  },
  pokemon: {
    types: {
      label: 'Type',
      options: ['Grass', 'Fire', 'Water', 'Electric', 'Psychic', 'Fighting', 'Dark', 'Steel', 'Dragon', 'Fairy', 'Normal'],
    },
    special: {
      label: 'Special',
      options: ['Energy', 'Supporter', 'Item', 'Pokemon Tool', 'Stadium'],
    },
  },
};

const categoryDisplayNames = {
  yugioh: 'Yu-Gi-Oh! Singles',
  onepiece: 'One Piece Singles',
  pokemonjapan: 'Pokemon Japan Singles',
  riftbound: 'Riftbound Singles',
};

const sortToApiValue = {
  name: 'name',
  'price-low': 'price_asc',
  'price-high': 'price_desc',
  newest: 'newest',
};

const Shop = () => {
  const { category } = useParams();
  const [searchParams] = useSearchParams();
  const presetSet = searchParams.get('set');
  const [viewMode, setViewMode] = useState('grid');
  const [sortBy, setSortBy] = useState('name');
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [showFilters, setShowFilters] = useState(() => window.innerWidth >= 1024);
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [storeOnly, setStoreOnly] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [catalogItems, setCatalogItems] = useState([]);
  const [imagesLoading, setImagesLoading] = useState(false);
  const [imagesError, setImagesError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [totalResults, setTotalResults] = useState(0);

  const [selectedSubCategories, setSelectedSubCategories] = useState([]);
  const [selectedSets, setSelectedSets] = useState([]);
  const [setQuery, setSetQuery] = useState('');
  const [sets, setSets] = useState({ pokemon: [], magic: [] });
  const { locations, selectedLocation, setSelectedLocation } = useStore();
  const selectedLocationIds = selectedLocation ? [selectedLocation.id] : [];

  const fetchCatalogImages = useCallback(async (apiCategories, sort = 'name', locationIds = [], limit = 10, offset = 0, subcategories = [], sets = [], onlineOnlyFlag = false, storeOnlyFlag = false) => {
    setImagesLoading(true);
    setImagesError(null);
    try {
      const apiSort = sortToApiValue[sort] || 'name';
      const data = await apiService.getCatalogImages(limit, offset, apiCategories, apiSort, locationIds, subcategories, sets, [], onlineOnlyFlag, storeOnlyFlag);
      const items = data.items.map((item) => ({
        name: item.name,
        description: item.description,
        description_html: item.description_html,
        price: item.price_money ? item.price_money.amount / 100 : 0,
        imageUrl: item.image_urls?.[0] || null,
        categories: item.categories || [],
        updatedAt: item.updated_at || null,
        variation_id: item.variation_id,
        variation_name: item.variation_name,
        location_id: item.location_id,
        present_at_location_ids: item.present_at_location_ids || [],
        quantities_by_location: item.quantities_by_location || {},
        online_available: item.online_available === true,
      }));
      setCatalogItems(items);
      setHasMore(Boolean(data.has_more));
      setTotalResults(data.total ?? 0);
    } catch (err) {
      setImagesError(err.message);
    } finally {
      setImagesLoading(false);
    }
  }, []);

  // Switching category clears the filters; a ?set= link (from the nav menu) preselects that set.
  useEffect(() => {
    setSelectedSubCategories([]);
    setSelectedSets(presetSet ? [presetSet] : []);
    setSetQuery('');
  }, [category, presetSet]);

  useEffect(() => {
    apiService.getSets().then(data => setSets(data)).catch(() => {});
  }, []);

  useEffect(() => {
    const baseCategories = categoryToApiFilter[category];
    if (baseCategories) {
      setCurrentPage(0);
      fetchCatalogImages(baseCategories, sortBy, selectedLocationIds, itemsPerPage, 0, selectedSubCategories, selectedSets, onlineOnly, storeOnly);
    } else {
      setCatalogItems([]);
    }
  }, [category, sortBy, selectedSubCategories, selectedSets, selectedLocation, itemsPerPage, onlineOnly, storeOnly, fetchCatalogImages]);

  const handleNextPage = () => {
    const baseCategories = categoryToApiFilter[category];
    if (!baseCategories || !hasMore) return;
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    fetchCatalogImages(baseCategories, sortBy, selectedLocationIds, itemsPerPage, nextPage * itemsPerPage, selectedSubCategories, selectedSets, onlineOnly, storeOnly);
    window.scrollTo(0, 0);
  };

  const handlePreviousPage = () => {
    const baseCategories = categoryToApiFilter[category];
    if (!baseCategories || currentPage === 0) return;
    const prevPage = currentPage - 1;
    setCurrentPage(prevPage);
    fetchCatalogImages(baseCategories, sortBy, selectedLocationIds, itemsPerPage, prevPage * itemsPerPage, selectedSubCategories, selectedSets, onlineOnly, storeOnly);
    window.scrollTo(0, 0);
  };

  const toggleSubCategory = (subCat) => {
    setSelectedSubCategories(prev =>
      prev.includes(subCat) ? prev.filter(c => c !== subCat) : [...prev, subCat]
    );
  };

  const toggleSet = (setName) => {
    setSelectedSets(prev =>
      prev.includes(setName) ? prev.filter(s => s !== setName) : [...prev, setName]
    );
  };

  const getSubCategoryGroups = () => {
    if (!category || !subCategoryFilters[category]) return [];
    const config = subCategoryFilters[category];
    if (config.label) {
      return [{ label: config.label, options: config.options }];
    }
    return Object.values(config);
  };

  const categories = [
    'Pokemon',
    'Magic',
    'Yu-Gi-Oh',
    'One Piece',
    'Digimon',
    'Accessories',
  ];

  const sortOptions = [
    { value: 'name', label: 'Name: A to Z' },
    { value: 'price-low', label: 'Price: Low to High' },
    { value: 'price-high', label: 'Price: High to Low' },
    { value: 'newest', label: 'Newest First' },
  ];

  const perPageOptions = [25, 50, 100];

  const toggleCategory = (cat) => {
    setSelectedCategories(prev =>
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
  };

  // Landing page when no category is selected
  if (!category) {
    const shopCategories = [
      {
        label: 'Trading Card Games',
        description: 'Pokémon, Magic: The Gathering, and more sealed product.',
        image: marvelMtg,
        link: '/shop/tradingcardgames',
        gradient: 'from-red-600 to-yellow-500',
      },
      {
        label: 'Pokémon Singles',
        description: 'Individual Pokémon cards from all sets.',
        image: pokemonLogo,
        link: '/shop/pokemon',
        gradient: 'from-yellow-400 to-red-500',
      },
      {
        label: 'MTG Singles',
        description: 'Individual Magic: The Gathering cards from all sets.',
        image: mtgLogo,
        link: '/shop/magic',
        gradient: 'from-blue-800 to-gray-900',
      },
      {
        label: 'Table Top Games',
        description: 'Warhammer 40,000 miniatures and tabletop wargaming products.',
        image: warhammer40k,
        link: '/shop/tabletopgames',
        gradient: 'from-gray-700 to-red-800',
      },
    ];

    return (
      <div className="min-h-screen bg-gray-50 pt-24 pb-16">
        <div className="container">
          <div className="mb-10">
            <h1 className="text-4xl font-bold mb-2">Shop</h1>
            <p className="text-gray-600">Browse our available categories below.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {shopCategories.map((cat) => (
              <motion.div
                key={cat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ y: -4 }}
                transition={{ duration: 0.3 }}
              >
                <Link
                  to={cat.link}
                  className="block bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow"
                >
                  <div className={`relative h-48 bg-gradient-to-br ${cat.gradient} flex items-center justify-center p-6`}>
                    <img src={cat.image} alt={cat.label} className="max-h-full max-w-full object-contain" />
                  </div>
                  <div className="p-5">
                    <h2 className="text-xl font-bold mb-1">{cat.label}</h2>
                    <p className="text-sm text-gray-500">{cat.description}</p>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-16">
      <div className="container">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold mb-2">
            {category ? (categoryDisplayNames[category] || category.charAt(0).toUpperCase() + category.slice(1)) : 'All Products'}
          </h1>
          {categoryToApiFilter[category] && totalResults > 0 && (
            <p className="text-gray-600">
              {currentPage * itemsPerPage + 1}–{Math.min((currentPage + 1) * itemsPerPage, totalResults)} of {totalResults} results
            </p>
          )}
        </div>

        {/* Controls Bar */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            {/* Left Controls */}
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${showFilters ? 'bg-primary-100 text-primary-600 border-primary-300' : 'border-gray-200 hover:bg-gray-50'}`}
              >
                <Filter className="w-4 h-4" />
                <span>Filters</span>
              </button>

              <div className="flex items-center gap-2 border-l pl-4">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded ${viewMode === 'grid' ? 'bg-primary-100 text-primary-600' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <Grid className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded ${viewMode === 'list' ? 'bg-primary-100 text-primary-600' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  <List className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Right Controls */}
            <div className="flex items-center gap-4">
              {/* Sort Dropdown */}
              <div className="relative">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="appearance-none bg-white border border-gray-200 rounded-lg px-4 py-2 pr-8 focus:outline-none focus:border-primary-500"
                >
                  {sortOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 pointer-events-none text-gray-400" />
              </div>

              {/* Items Per Page Dropdown */}
              <div className="hidden md:flex items-center gap-2">
                <span className="text-gray-700">Show:</span>
                <div className="relative">
                  <select
                    value={itemsPerPage}
                    onChange={(e) => setItemsPerPage(Number(e.target.value))}
                    className="appearance-none bg-white border border-gray-200 rounded-lg px-4 py-2 pr-8 focus:outline-none focus:border-primary-500"
                  >
                    {perPageOptions.map(option => (
                      <option key={option} value={option}>
                        {option} per page
                      </option>
                    ))}
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 pointer-events-none text-gray-400" />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-6">
          {/* Filters Sidebar */}
          <AnimatePresence>
            {showFilters && (
              <motion.aside
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="w-64 flex-shrink-0"
              >
                <div className="bg-white rounded-lg shadow-sm p-6">
                  {/* Store Location — always at top when on a product page */}
                  {categoryToApiFilter[category] && locations.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Store Location</h3>
                      <div className="space-y-2">
                        {locations.map(loc => (
                          <label key={loc.id} className="flex items-start gap-2 cursor-pointer">
                            <input
                              type="radio"
                              name="store-location-shop"
                              checked={selectedLocation?.id === loc.id}
                              onChange={() => {}}
                              onClick={() => setSelectedLocation(selectedLocation?.id === loc.id ? null : loc)}
                              className="mt-0.5 w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500 flex-shrink-0"
                            />
                            <div>
                              <div className="text-sm font-medium text-gray-800">{loc.name}</div>
                              <div className="text-xs text-gray-500">{loc.address?.address_line1}, {loc.address?.locality}, {loc.address?.administrative_district_level1}</div>
                            </div>
                          </label>
                        ))}
                        {selectedLocation && (
                          <button
                            onClick={() => setSelectedLocation(null)}
                            className="text-xs text-gray-400 hover:text-gray-600 mt-1"
                          >
                            Show all locations
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Availability Filter */}
                  <div className="border-t pt-5 mb-6">
                    <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Availability</h3>
                    <div className="space-y-2">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={onlineOnly}
                          onChange={e => { setOnlineOnly(e.target.checked); if (e.target.checked) setStoreOnly(false); }}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700">Available Online</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={storeOnly}
                          onChange={e => { setStoreOnly(e.target.checked); if (e.target.checked) setOnlineOnly(false); }}
                          className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                        />
                        <span className="text-sm text-gray-700">In Store Only</span>
                      </label>
                    </div>
                  </div>

                  {/* Categories Filter - hidden on magic/pokemon pages */}
                  {!categoryToApiFilter[category] && (
                    <div className="mb-6">
                      <h3 className="font-semibold mb-3">Categories</h3>
                      <div className="space-y-2">
                        {categories.map(cat => (
                          <label key={cat} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={selectedCategories.includes(cat)}
                              onChange={() => toggleCategory(cat)}
                              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                            />
                            <span className="ml-2 text-sm">{cat}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Sub-Category Filters (hierarchical) */}
                  {getSubCategoryGroups().map(group => (
                    <div key={group.label} className="mb-6">
                      <h3 className="font-semibold mb-3">{group.label}</h3>
                      <div className="space-y-2">
                        {group.options.map(subCat => (
                          <label key={subCat} className="flex items-center">
                            <input
                              type="checkbox"
                              checked={selectedSubCategories.includes(subCat)}
                              onChange={() => toggleSubCategory(subCat)}
                              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                            />
                            <span className="ml-2 text-sm">{subCat}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}

                  {/* Set Filter */}
                  {categoryToApiFilter[category] && sets[category]?.length > 0 && (
                    <div className="mb-6">
                      <h3 className="font-semibold mb-3">Set</h3>
                      <FilterSearchInput value={setQuery} onChange={setSetQuery} placeholder="Search sets..." />
                      <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                        {sets[category]
                          .filter(setName => selectedSets.includes(setName) || setName.toLowerCase().includes(setQuery.trim().toLowerCase()))
                          .map(setName => (
                          <label key={setName} className="flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selectedSets.includes(setName)}
                              onChange={() => toggleSet(setName)}
                              className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 flex-shrink-0"
                            />
                            <span className="ml-2 text-sm">{setName}</span>
                          </label>
                        ))}
                        {setQuery.trim() && !sets[category].some(n => n.toLowerCase().includes(setQuery.trim().toLowerCase())) && (
                          <p className="text-sm text-gray-500">No sets match "{setQuery.trim()}"</p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Clear Filters */}
                  <button
                    onClick={() => {
                      setSelectedCategories([]);
                      setSelectedSubCategories([]);
                      setSelectedSets([]);
                      setSetQuery('');
                      setSelectedLocation(null);
                      setOnlineOnly(false);
                      setStoreOnly(false);
                    }}
                    className="w-full py-2 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Clear All Filters
                  </button>
                </div>
              </motion.aside>
            )}
          </AnimatePresence>

          {/* Products Grid/List */}
          <div className="flex-1 min-h-screen">
            {imagesLoading ? (
              <div className="flex items-center justify-center py-24">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
              </div>
            ) : imagesError ? (
              <div className="text-center py-12">
                <p className="text-red-500 text-lg">{imagesError}</p>
              </div>
            ) : catalogItems.length > 0 ? (
              <>
                <div className={viewMode === 'grid'
                  ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'
                  : 'space-y-4'
                }>
                  {catalogItems.map((item, index) => (
                    <motion.div
                      key={item.variation_id || item.name}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: index * 0.05 }}
                    >
                      <ProductCard
                        product={{ name: item.name, category: category, price: item.price, set: item.description, description: item.description, categories: item.categories, updatedAt: item.updatedAt, variation_id: item.variation_id, variation_name: item.variation_name, location_id: item.location_id, present_at_location_ids: item.present_at_location_ids, quantities_by_location: item.quantities_by_location, selectedLocationIds, online_available: item.online_available }}
                        imageUrl={item.imageUrl}
                        viewMode={viewMode}
                        showCondition={Boolean(categoryToApiFilter[category])}
                      />
                    </motion.div>
                  ))}
                </div>

                {/* Pagination Controls */}
                {(currentPage > 0 || hasMore) && (
                  <div className="flex items-center justify-center gap-3 mt-8">
                    {currentPage > 0 && (
                      <button
                        onClick={handlePreviousPage}
                        className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-gray-700"
                      >
                        <ArrowLeft className="w-4 h-4" />
                        <span>Previous Page</span>
                      </button>
                    )}
                    {hasMore && (
                      <button
                        onClick={handleNextPage}
                        className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors text-gray-700"
                      >
                        <span>Next Page</span>
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <p className="text-gray-500 text-lg">No products found.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Shop;
